import React, { useCallback } from "react";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Masthead, { MastheadMenuButton, MastheadTitle } from "@amzn/meridian/masthead";
import Theme from "@amzn/meridian/theme";
import Toggle from "@amzn/meridian/toggle";
import exportSmallTokens from "@amzn/meridian-tokens/base/icon/export-small";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import arLogo from "@/app/components/Masthead/AR_Logo_RGB_2color_reversed.png";
import { RadSiteSelector } from "@/app/components/Masthead/RadSiteSelector";
import { REDIRECT_LINKS, ROUTES } from "@/app/app-constants";
import LanguagePicker from "@/features/yard360/LanguagePicker";
import { useDebugModeQuery } from "@/hooks/useDebugModeQuery";
import { useRyteDebugTimeRangeQuery } from "@/hooks/useRyteDebugTimeRangeQuery";
import { nameSpaces } from "@/i18n";
import { keys as globalKeys } from "@/i18n/keys";
import { keys as ryteKeys } from "@/pages/ryteTracker/i18n";
import { useAppSelector } from "@/store/hooks";
import type { RootState } from "@/store/types";
import { isAllowListedAuthInfo } from "@/utils/allowListUtils";
import { buildLegacyEventHistoryUrl } from "@/utils/urlBuilders";

interface MastheadWrapperProps {
  onMenuClick: () => void;
}

const selectAuthInfo = (state: RootState) => state.authInfo;

const MastheadWrapper: React.FC<MastheadWrapperProps> = ({ onMenuClick }) => {
  const { t: tGlobal } = useTranslation();
  const { t: tRyte } = useTranslation(nameSpaces.ryteTracker);
  const { pathname } = useLocation();
  const authInfo = useAppSelector(selectAuthInfo);
  const isAllowListed = isAllowListedAuthInfo(authInfo);
  const { isDebugMode, setDebugMode } = useDebugModeQuery();
  const { hasConfiguredRange, disableDebugMode } = useRyteDebugTimeRangeQuery();
  const isLocalDevelopment = process.env.NODE_ENV === "development";
  const isProduction = authInfo.domain === "prod" && !isLocalDevelopment;
  const isRadRoute = pathname === ROUTES.RYTE_TRACKER;
  const tcpBaseUrl = REDIRECT_LINKS[authInfo.realm]?.[authInfo.domain]?.TCP_BASEURL ?? "";
  const legacyEventHistoryUrl = buildLegacyEventHistoryUrl(tcpBaseUrl);
  const shouldShowOldViewButton =
    pathname === ROUTES.EVENT_REPORT && Boolean(legacyEventHistoryUrl);
  const shouldShowDebugToggle = isAllowListed && (!isProduction || isDebugMode);
  const handleDebugModeChange = useCallback(
    (isEnabled: boolean) => {
      if (!isEnabled && hasConfiguredRange) {
        disableDebugMode();
        return;
      }
      setDebugMode(isEnabled);
    },
    [disableDebugMode, hasConfiguredRange, setDebugMode]
  );

  return (
    <Theme mode="dark">
      <Masthead>
        <MastheadMenuButton onClick={onMenuClick} open />
        <MastheadTitle href="#">
          <img src={arLogo} alt="Amazon relay" style={{ height: "24px" }} />
        </MastheadTitle>
        {shouldShowDebugToggle && (
          <Toggle
            checked={isDebugMode}
            onChange={handleDebugModeChange}
            data-testid="ryte-debug-mode-toggle"
            aria-label={tRyte(ryteKeys.RYTE_DEBUG_MODE_TOGGLE)}
          >
            {tRyte(ryteKeys.RYTE_DEBUG_MODE_TOGGLE)}
          </Toggle>
        )}
        {shouldShowOldViewButton && (
          <Button
            href={legacyEventHistoryUrl}
            target="_blank"
            rel="noopener noreferrer"
            type="icon"
            size="small"
            data-testid="event-report-old-view"
          >
            <Icon tokens={exportSmallTokens} />
            {tGlobal(globalKeys.EVENT_REPORT_VISIT_OLD_VIEW)}
          </Button>
        )}
        <LanguagePicker />
        {isRadRoute ? <RadSiteSelector /> : null}
      </Masthead>
    </Theme>
  );
};

export default MastheadWrapper;
